import React, { useState } from 'react';
import './TourSingle.css';

// Import necessary components
import { Comments, Lightbox, PageSidebar, PageTitle } from '../../../components';
import { calculateAverageRating } from '../../../utils';

import { useLocation } from 'react-router-dom';

// Import FontAwesome icon
import { FaArrowRight, FaCircleNotch, FaStar } from 'react-icons/fa';
import { FaCircleCheck, FaCircleXmark, FaLocationDot } from 'react-icons/fa6';



const TourSingle = () => {

  // State to manage the active tab (e.g., overview, details, etc.)
  const [activeTab, setActiveTab] = useState('overview');

  // Function to handle tab switching
  const handleTabClick = (tab) => {
    setActiveTab(tab);
  };

  // Get tour data passed from the previous page
  const location = useLocation();
  const tour = location.state;

  // Calculate the average rating of the tours based on user reviews
  const averageRating = calculateAverageRating(tour);

  // Function to handle form submission
  const handleSubmit = (event) => {
    event.preventDefault(); // Prevent the default form submission behavior
    event.target.reset(); // Reset the form fields
  };

  return (
    <>

      {/* Page Title Section */}
      <PageTitle title={tour.title} page={'Tour Single'} />

      <section className='tour-single page-single'>

        <div className="tour-info page-info">

          {/* Tour Image */}
          <div className="image">
            <img src={tour.image} alt={tour.title} />
          </div>

          {/* TourContent */}
          <div className="content tab-info">

            {/* Tour Info Tabs - Tab Buttons */}
            <div className="tour-info-tabs tab-buttons">
              <button 
                type="button" 
                className={`button ${activeTab === 'overview' ? 'active' : ''}`} 
                onClick={() => handleTabClick('overview')}
              >
                Overview
              </button>
              <button 
                type="button" 
                className={`button ${activeTab === 'tour-plan' ? 'active' : ''}`} 
                onClick={() => handleTabClick('tour-plan')}
              >
                Tour Plan
              </button>
              <button 
                type="button" 
                className={`button ${activeTab === 'location' ? 'active' : ''}`} 
                onClick={() => handleTabClick('location')}
              >
                Location
              </button>
              <button 
                type="button" 
                className={`button ${activeTab === 'gallery' ? 'active' : ''}`} 
                onClick={() => handleTabClick('gallery')}
              >
                Gallery
              </button>
              <button 
                type="button" 
                className={`button ${activeTab === 'reviews' ? 'active' : ''}`} 
                onClick={() => handleTabClick('reviews')}
              >
                Reviews
              </button>
            </div>

            {/* Tour Info Tabs - Tab Sections */}
            <div className="tab-sections">

              {/* Overview Tab Section */}
              {activeTab === 'overview' && (

                <div className="overview tab-section active" id="overview">
            
                  {/* Tour heading */}
                  <h3 className='main-heading'>{tour.title}</h3>

                  {/* Tour rating */}
                  <div className='tour-rating'>
                    <div className='icon'>
                        {/* Display star icons for rating */}
                        <FaStar /> <FaStar /> <FaStar /> <FaStar /> <FaStar />
                    </div>
                    {/* Display average rating and number of comments */}
                    {tour.comments.length > 0 ? (
                        <h5>{averageRating} ({tour.comments.length})</h5>
                    ) : (
                        <h5>Not rated</h5>
                    )}
                  </div>

                  {/* Tour Content */}
                  {tour.overviews.map((overview, index) => (
                    <p key={index}>{overview}</p>
                  ))}

                  {/* Highlights of the tour */}
                  <div className='sub-section'>
        
                    <h3 className='sub-heading'>Highlights of the tour:</h3>
        
                    <p>The highlights of the {tour.title} tour include the following key features that will make your experience unforgettable:</p>
        
                    {/* List of Highlights */}
                    <ul>
                      {tour.highlights.map((highlight, index) => {
                        return <li key={index}><FaArrowRight className='icon' />{highlight}</li>
                      })}
                    </ul>
        
                  </div>

                  {/* Included/Excluded */}
                  <div className='sub-section'>

                    <h3 className='sub-heading'>Included/Excluded:</h3>

                    <p>By choosing {tour.title}, you'll know exactly what is included and excluded, with the following details:</p>

                    <div className='box-container'>
                      {/* List of Included */}
                      <ul>
                        {tour.included.map((include, index) => {
                          return <li key={index}><div className='icon'><FaCircleCheck /></div> {include}</li>
                        })}
                      </ul>
                      {/* List of Excluded */}
                      <ul>
                        {tour.excluded.map((exclude, index) => {
                          return <li key={index}><div className='icon'><FaCircleXmark /></div>{exclude}</li>
                        })}
                      </ul>
                    </div>

                  </div>

                </div>
              )}

              {/* Tour Plan Tab Section */}
              {activeTab === 'tour-plan' && (
                <div className="tour-plan tab-section" id="tour-plan">
                  <div className="box-container">
                    {tour.tourPlan.map((plan, index) => (
                      <div className="plan-item" key={index}>
                        <div className='location-icon'><FaLocationDot /></div>
                        <div className="plan-content">
                          <h3>{plan.day}</h3>
                          <p>{plan.description}</p>
                          <ul>
                            {plan.activities.map((activity, idx) => (
                              <li key={idx}>
                                <FaCircleNotch className="icon" />
                                <span>{activity}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Location Tab Section */}
              {activeTab === 'location' && (
                <div className="tour-location tab-section">
                  <p>{tour.location.description}</p>
                  <iframe
                  src={tour.location.map}
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  title="Tour Location Map"
                ></iframe> 
                </div>
              )}

              {/* Gallery Tab Section */}
              {activeTab === 'gallery' && (
                <div className="tab-section">
                  {/* Tour Image gallery */}
                  <Lightbox images={tour.gallery} />
                </div>
              )}

              {/* Reviews Tab Section */}
              {activeTab === 'reviews' && (
                <div className="reviews tab-section" id="reviews">

                  {/* Reviews */}
                  <Comments comments={tour.comments} />

                  <div className='product-review'>
                    {/* Leave a Comment Form */}
                    <form className='form' onSubmit={handleSubmit}>

                      <h2>leave a comment</h2> {/* Comment Form Title */}

                      <div className='input-box'>
                        <input type="text" name="name" className='box' id="name" placeholder="name" required /> {/* Name Input Field */}
                        <input type="email" name="email" className='box' id="email" placeholder="email" required /> {/* Email Input Field */}
                      </div>
                      
                      <textarea cols="30" rows="10" name="comment" className='box' id="comment" placeholder="comment"></textarea> {/* Comment Textarea */}
                      
                      <button type="submit" name="submit" id="submit" className='btn'>submit</button> {/* Submit Button */}

                    </form>

                  </div>

                </div>
              )}

            </div>
          </div>
        </div>

        {/* Sidebar Section */}
        <PageSidebar
          heading={'Tour'}
          details={tour.details}
          button={{ link: 'Booking', title: 'Book Now' }}
        />

      </section>

     </> 
  );
};

export default TourSingle;